import WebApp from '@twa-dev/sdk';

let initPromise = null;
let cloudAvailable = 'unknown';

export function initTwa() {
  if (initPromise) return initPromise;
  initPromise = (async () => {
    try {
      WebApp.ready();
      WebApp.expand();
      WebApp.disableVerticalSwipes();
      WebApp.enableClosingConfirmation();
    } catch {
      // running outside Telegram (dev); safe to ignore
    }
    applyTheme();
    try {
      if (WebApp.CloudStorage && typeof WebApp.CloudStorage.getKeys === 'function') {
        cloudAvailable = 'available';
      }
    } catch {
      cloudAvailable = 'unavailable';
    }
  })();
  return initPromise;
}

export function applyTheme() {
  try {
    const theme = WebApp.themeParams || {};
    const root = document.documentElement;
    for (const [k, v] of Object.entries(theme)) {
      if (v) root.style.setProperty(`--tg-theme-${k.replace(/_/g, '-')}`, v);
    }
  } catch {
    // ignore
  }
}

function parseTwaInitDataFromHash() {
  try {
    const hash = typeof window !== 'undefined' ? window.location?.hash : '';
    if (!hash || hash.length < 2) return '';
    const body = hash.charAt(0) === '#' ? hash.slice(1) : hash;
    const key = 'tgWebAppData=';
    const i = body.indexOf(key);
    if (i === -1) return '';
    const from = i + key.length;
    const amp = body.indexOf('&', from);
    const raw = amp === -1 ? body.slice(from) : body.slice(from, amp);
    return decodeURIComponent(raw);
  } catch {
    return '';
  }
}

export function getInitData() {
  let live = '';
  try {
    live = WebApp.initData || '';
  } catch {
    live = '';
  }
  if (live) return live;
  return parseTwaInitDataFromHash();
}

export function getInitDataUnsafe() {
  try {
    return WebApp.initDataUnsafe || {};
  } catch {
    return {};
  }
}

export function hapticFeedback(style = 'light') {
  try {
    if (WebApp.HapticFeedback) WebApp.HapticFeedback.impactOccurred(style);
  } catch {
    // ignore
  }
}

export function showBackButton(onClick) {
  try {
    WebApp.BackButton.show();
    if (typeof WebApp.BackButton.offClick === 'function') WebApp.BackButton.offClick();
    WebApp.BackButton.onClick(onClick);
    return () => {
      try {
        if (typeof WebApp.BackButton.offClick === 'function') WebApp.BackButton.offClick();
        WebApp.BackButton.hide();
      } catch {
        // ignore
      }
    };
  } catch {
    return () => {};
  }
}

export function hideBackButton() {
  try {
    if (typeof WebApp.BackButton.offClick === 'function') WebApp.BackButton.offClick();
    WebApp.BackButton.hide();
  } catch {
    // ignore
  }
}

export function showMainButton(text, onClick) {
  try {
    WebApp.MainButton.setText(text);
    WebApp.MainButton.show();
    if (typeof WebApp.MainButton.offClick === 'function') WebApp.MainButton.offClick();
    WebApp.MainButton.onClick(onClick);
    return () => {
      try {
        if (typeof WebApp.MainButton.offClick === 'function') WebApp.MainButton.offClick();
        WebApp.MainButton.hide();
      } catch {
        // ignore
      }
    };
  } catch {
    return () => {};
  }
}

export function hideMainButton() {
  try {
    if (typeof WebApp.MainButton.offClick === 'function') WebApp.MainButton.offClick();
    WebApp.MainButton.hide();
  } catch {
    // ignore
  }
}

export function setMainButtonParams(params) {
  try {
    if (!params || typeof params !== 'object') return;
    if (Object.prototype.hasOwnProperty.call(params, 'text')) WebApp.MainButton.setText(params.text);
    if (params.color) WebApp.MainButton.setParams({ color: params.color });
    if (params.textColor) WebApp.MainButton.setParams({ text_color: params.textColor });
    if (typeof params.visible === 'boolean') {
      if (params.visible) WebApp.MainButton.show();
      else WebApp.MainButton.hide();
    }
    if (typeof params.enabled === 'boolean') {
      if (params.enabled) WebApp.MainButton.enable();
      else WebApp.MainButton.disable();
    }
  } catch {
    // ignore
  }
}

export function openTelegramLink(url) {
  try {
    WebApp.openTelegramLink(url);
  } catch {
    window.location.href = url;
  }
}

export function closeApp() {
  try {
    WebApp.close();
  } catch {
    window.close();
  }
}

export async function readToken() {
  if (cloudAvailable === 'available') {
    try {
      const value = await new Promise((resolve, reject) => {
        WebApp.CloudStorage.getItem('token', (err, data) => {
          if (err) reject(err);
          else resolve(data);
        });
      });
      if (value) return value;
    } catch {
      // fall through to localStorage
    }
  }
  try {
    return localStorage.getItem('token') || '';
  } catch {
    return '';
  }
}

export async function writeToken(token) {
  if (cloudAvailable === 'available') {
    try {
      await new Promise((resolve, reject) => {
        WebApp.CloudStorage.setItem('token', token, (err) => {
          if (err) reject(err);
          else resolve();
        });
      });
    } catch {
      // ignore
    }
  }
  try {
    localStorage.setItem('token', token);
  } catch {
    // ignore
  }
}

export async function clearToken() {
  if (cloudAvailable === 'available') {
    try {
      await new Promise((resolve, reject) => {
        WebApp.CloudStorage.removeItem('token', (err) => {
          if (err) reject(err);
          else resolve();
        });
      });
    } catch {
      // ignore
    }
  }
  try {
    localStorage.removeItem('token');
  } catch {
    // ignore
  }
}

export function getUsername() {
  return getInitDataUnsafe().user?.username || getInitDataUnsafe().user?.first_name || '';
}

export function getBotUsername() {
  try {
    return WebApp.initDataUnsafe?.bot?.username || '';
  } catch {
    return '';
  }
}

export function getLaunchDiag() {
  try {
    const tg = typeof window !== 'undefined' ? window.Telegram : undefined;
    const liveInit = (() => { try { return WebApp.initData || ''; } catch { return ''; } })();
    const hashInit = parseTwaInitDataFromHash();
    return {
      has_telegram: !!tg,
      has_webapp: !!(tg && tg.WebApp),
      init_len: (getInitData() || '').length,
      live_init_len: liveInit.length,
      hash_init_len: hashInit.length,
      start_param: getInitDataUnsafe().start_param || null,
      url: typeof location !== 'undefined' ? location.href : null,
      ua: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    };
  } catch (e) {
    return { diag_error: String(e) };
  }
}
